/* =========================================================================
   PATH v1 — the home "next station" surface (PLAN-levels-path.md).

   A pure PRESENTATION layer over the already-shipped pilgrimage engine
   (pilgrimage.ts + data/pilgrimageData.ts). It never advances a gate, never
   grants a reward, and never touches the round engine — it reads the engine's
   live frontier (pilgrimageState) and repaints three home surfaces:

     • the PRIMARY CTA (#startBtn) — relabelled "Station N of 10 / <title> /
       <requirement>", the same visual weight and placement "Start Round" had.
       Tapping it still runs startOrResume(): the SAME round-based game, only
       framed as the next station. A live Resume snapshot always wins the label
       (resuming a real run is the truer action); the station then shows on the
       line above the button.
     • the stations-completed meter, beside the existing 🔥 daily streak (same
       source as Theo's card chip — profile stats.currentStreak; guests have no
       server streak, so the chip simply stays off).
     • the next station's REWARD PREVIEW — its collectible + the cents the
       server grants on completion (usp_PilgrimageStep_Complete).

   Collectible art: the 8 Apostle medallions (media/collectibles/<key>.png) are
   still being generated. We probe for them exactly like characters/ui.ts probes
   Theo's poses — and only when hasOptionalMedia() says the batch has landed, so
   a missing file can never cost a boot-time 404. Until then the bundled emoji
   face from the Collections catalog renders (🔑 St. Peter …), falling back to
   the generic 🎉 badge for any key the catalog doesn't know.
   ========================================================================= */
import { esc, fmtCents } from './engine';
import { icon } from './ui/icons';
import { countTarget, parseCriteria } from './criteria';
import { pilgrimageState, requirementText } from './pilgrimage';
import { getProfileMe, isAuthenticated, type PilgrimageStep } from './api';
import { BUNDLED_SETS } from './data/collectionsData';
import { hasOptionalMedia, isPilgrimageEnabled } from './featureFlags';
import { currentIdentity, readSnapshot } from './resumeGame';
import { etDateString } from './today';
import { openStreakSheet, renderStreakCard, type StreakData } from './ui/streak';

/* ------------------------------- pure logic ------------------------------- */

export interface NextStation {
  step: PilgrimageStep;
  /** 0-based position in the pilgrimage. */
  index: number;
  /** 1-based station number as shown to the player ("Station 3"). */
  number: number;
}

/** The station the player is walking toward: the FIRST incomplete step (steps
    complete strictly in order, so first-incomplete IS the frontier). Returns
    null once the whole pilgrimage is walked. PURE. */
export function nextStation(steps: PilgrimageStep[], completed: Set<string>): NextStation | null {
  for (let i = 0; i < steps.length; i++) {
    if (!completed.has(steps[i].key)) return { step: steps[i], index: i, number: i + 1 };
  }
  return null;
}

export interface PathProgress {
  done: number;
  total: number;
  /** 0-100, rounded — the meter's fill width. */
  pct: number;
}

/** Stations completed in this tier. Only keys belonging to `steps` count, so a
    stale/foreign key in the local store can never inflate the bar. PURE. */
export function pathProgress(steps: PilgrimageStep[], completed: Set<string>): PathProgress {
  const total = steps.length;
  const done = steps.reduce((n, s) => n + (completed.has(s.key) ? 1 : 0), 0);
  return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) };
}

export interface StepReward {
  collectible: string | null;
  cents: number;
  item: string | null;
  qty: number;
}

/** Parse a step's RewardJson defensively (malformed JSON → an empty reward, never
    a throw — the CTA must still render). PURE. */
export function parseStepReward(json: string | null | undefined): StepReward {
  const empty: StepReward = { collectible: null, cents: 0, item: null, qty: 0 };
  if (!json) return empty;
  try {
    const r = JSON.parse(json) as Partial<Record<keyof StepReward, unknown>>;
    if (!r || typeof r !== 'object') return empty;
    const item = typeof r.item === 'string' ? r.item : null;
    return {
      collectible: typeof r.collectible === 'string' ? r.collectible : null,
      cents: typeof r.cents === 'number' && isFinite(r.cents) ? r.cents : 0,
      item,
      qty: typeof r.qty === 'number' && isFinite(r.qty) ? r.qty : item ? 1 : 0,
    };
  } catch {
    return empty;
  }
}

/** Catalog face + title for a collectible key, from the bundled Collections
    catalog. Unknown keys get an empty icon (faceHTML draws the brand sparkle). PURE. */
export function collectibleFace(key: string): { icon: string; title: string } {
  for (const set of BUNDLED_SETS) {
    const hit = set.items.find((i) => i.key === key);
    if (hit) return { icon: hit.icon || '', title: hit.title };
  }
  return { icon: '', title: key };
}

/** "4 / 15"-style gate progress for a COUNTED gate worth showing. Single-shot
    gates ("finish a full game", "restore the altar") return null — "0 / 1" is
    noise, not progress. PURE. */
export function gateProgress(
  step: PilgrimageStep,
  counts: Record<string, number>,
): { cur: number; target: number } | null {
  const c = parseCriteria(step.gateCriteriaJson);
  if (!c || !c.conditions.some((x) => x.metric === 'Count')) return null;
  const target = countTarget(c);
  if (target <= 1) return null;
  return { cur: Math.min(target, counts[step.key] || 0), target };
}

/** The milestone CTA's three lines. PURE (so the copy is unit-testable).
    `total` stays in the signature for callers/tests even though the kicker no
    longer prints it ("Milestone N", not "Station N of M"). */
export function ctaLines(
  next: NextStation,
  _total: number,
  counts: Record<string, number>,
): { kicker: string; title: string; req: string } {
  const gp = gateProgress(next.step, counts);
  const req = requirementText(next.step) + (gp ? ` · ${gp.cur} / ${gp.target}` : '');
  // "Milestone", not "Station" — Eric's 2026-09-06 home mockup wording.
  return { kicker: `Milestone ${next.number}`, title: next.step.title, req };
}

/* -------------------------------- art probe -------------------------------- */

const ART_DIR = 'media/collectibles/';
const artAvail: Record<string, boolean> = {};

/** Probe a collectible's medallion; the emoji face stays until it resolves.
    Skipped entirely while the batch is unshipped (hasOptionalMedia) so a boot
    never fires a 404 for art that is known not to exist yet. */
function probeCollectibleArt(key: string, onReady: () => void): void {
  if (!hasOptionalMedia()) return;
  if (key in artAvail) return;
  artAvail[key] = false;
  try {
    const img = new Image();
    img.onload = () => {
      artAvail[key] = true;
      onReady();
    };
    img.onerror = () => {
      /* not generated yet — emoji face stays */
    };
    img.src = ART_DIR + key + '.png';
  } catch {
    /* no Image() (tests) */
  }
}

/** Reward-face markup: real medallion once it lands, emoji until then. */
function faceHTML(key: string): string {
  const face = collectibleFace(key);
  return artAvail[key]
    ? `<img class="path-reward-art" src="${ART_DIR}${esc(key)}.png" alt="${esc(face.title)}"/>`
    : `<span class="path-reward-ic">${face.icon ? esc(face.icon) : icon('sparkle', 20)}</span>`;
}

/* -------------------------------- rendering -------------------------------- */

/** Last known server streak (same value Theo's chip shows); guests keep null. */
let knownStreak: number | null = null;

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}
function setHidden(id: string, hidden: boolean): void {
  el(id)?.classList.toggle('hidden', hidden);
}

/** Repaint the home path surfaces. Safe to call whenever home is (re)shown —
    MUST run after renderResumeCta(), which owns the plain button label. */
export function renderPathHome(): void {
  const screen = el('screenHome');
  const host = el('pathHome');
  const btn = el('startBtn');
  if (!screen || !host || !btn) return; // no DOM (tests) / older shell

  // Flag off → the previous home, verbatim: no path chrome, no relabelled CTA.
  if (!isPilgrimageEnabled()) {
    screen.classList.remove('path-mode');
    host.classList.add('hidden');
    setHidden('pathReward', true);
    setHidden('pathMapLink', true);
    setHidden('homeStreak', true);
    btn.classList.remove('path-cta');
    return;
  }

  screen.classList.add('path-mode');
  host.classList.remove('hidden');

  const { pilg, completed, counts } = pilgrimageState();
  const prog = pathProgress(pilg.steps, completed);
  const next = nextStation(pilg.steps, completed);

  // Meter — stations completed in this pilgrimage.
  const fill = el('pathBarFill');
  if (fill) fill.style.width = `${prog.pct}%`;
  const bar = el('pathBar');
  if (bar) {
    bar.setAttribute('aria-valuemax', String(prog.total));
    bar.setAttribute('aria-valuenow', String(prog.done));
    bar.setAttribute('aria-label', `${prog.done} of ${prog.total} stations complete`);
  }
  const meterTxt = el('pathMeterTxt');
  if (meterTxt) meterTxt.textContent = `Your journey · ${prog.done} of ${prog.total} milestones`; // textContent — no esc needed

  // 🔥 daily streak — the SAME server value Theo's greeting chip shows.
  renderStreakChip();

  // The station line above the button (carries the station when a Resume
  // snapshot owns the button label, and the "walked it all" note at the end).
  const line = el('pathStationLine');
  const resuming = !!readSnapshot(currentIdentity());

  if (next) {
    const lines = ctaLines(next, prog.total, counts);
    if (resuming) {
      btn.classList.remove('path-cta');
      if (line) line.textContent = `Next: ${lines.kicker} · ${lines.title}`;
      setHidden('pathStationLine', false);
    } else {
      // The station IS the primary CTA (reference screenshot's "Level N" pill).
      btn.classList.add('path-cta');
      btn.innerHTML =
        `<span class="path-cta-kicker">${esc(lines.kicker)}</span>` +
        `<span class="path-cta-title">${esc(lines.title)}</span>` +
        `<span class="path-cta-req">${esc(lines.req)}</span>`;
      setHidden('pathStationLine', true);
    }
    renderReward(next.step);
  } else {
    // Every station walked — the round game stays exactly where it was.
    btn.classList.remove('path-cta');
    if (line) line.innerHTML = `${icon('sparkle', 16)} ${esc(pilg.title)}. Every station walked.`;
    setHidden('pathStationLine', false);
    setHidden('pathReward', true);
  }

  // The map is hidden for now (Eric, 2026-08-02): several of its nodes (e.g.
  // "A Word with a Friend") are hollow/unfinished — see TODO.md. Re-enable by
  // reverting to `currentTier() !== 1` once the map's own content is ready.
  setHidden('pathMapLink', true);
}

/** Last-known streak stats for this identity (card + sheet; see forgetPathStreak). */
let knownStats: { currentStreak: number; bestStreak: number; lastPlayedDate: string | null } | null = null;
/** The chip/card click handlers are attached once per document. */
let streakTapWired = false;

function streakData(): StreakData | null {
  if (!knownStats) return null;
  return { ...knownStats, today: etDateString() };
}

function wireStreakTaps(): void {
  if (streakTapWired) return;
  const open = (): void => {
    const d = streakData();
    if (d) void openStreakSheet(d);
  };
  const chip = el('pathStreak');
  const card = el('homeStreak');
  if (!chip && !card) return;
  chip?.addEventListener('click', open);
  card?.addEventListener('click', open);
  streakTapWired = true;
}

function paintStreak(): void {
  const chip = el('pathStreak');
  if (chip && knownStreak != null) {
    chip.innerHTML = `${icon('flame', 14)} ${knownStreak}`;
    chip.classList.remove('hidden');
    chip.setAttribute('role', 'button');
    chip.setAttribute('tabindex', '0');
    chip.setAttribute('aria-label', `${knownStreak}-day streak, details`);
  }
  renderStreakCard(el('homeStreak'), streakData());
}

function renderStreakChip(): void {
  const chip = el('pathStreak');
  if (!chip) return;
  if (!isAuthenticated()) {
    chip.classList.add('hidden'); // guests have no server streak (as on Theo's card)
    renderStreakCard(el('homeStreak'), null);
    return;
  }
  wireStreakTaps();
  if (knownStreak != null) paintStreak();
  void getProfileMe().then((r) => {
    if (!r.ok || !r.data.stats) return;
    const st = r.data.stats;
    knownStreak = st.currentStreak || 0;
    knownStats = {
      currentStreak: knownStreak,
      bestStreak: st.bestStreak || 0,
      // The API sends 'YYYY-MM-DD'; tolerate a full ISO timestamp from an older server.
      lastPlayedDate: st.lastPlayedDate ? String(st.lastPlayedDate).slice(0, 10) : null,
    };
    if (!isAuthenticated()) return; // signed out while in flight
    paintStreak();
  });
}

function renderReward(step: PilgrimageStep): void {
  const host = el('pathReward');
  if (!host) return;
  const reward = parseStepReward(step.rewardJson);
  if (!reward.collectible && reward.cents <= 0 && !reward.item) {
    host.classList.add('hidden');
    host.innerHTML = '';
    return;
  }
  if (reward.collectible) probeCollectibleArt(reward.collectible, () => renderPathHome());

  const parts: string[] = [];
  if (reward.collectible) {
    const face = collectibleFace(reward.collectible);
    parts.push(`${faceHTML(reward.collectible)}<span class="path-reward-name">${esc(face.title)}</span>`);
  }
  if (reward.cents > 0) parts.push(`<span class="path-reward-cash money">${esc(fmtCents(reward.cents))}</span>`);
  if (reward.item) parts.push(`<span class="path-reward-name">${icon('ticket', 14)} ${esc(reward.item.replace(/_/g, ' '))}</span>`);

  host.innerHTML = `<span class="path-reward-lbl">Reward</span>${parts.join('<span class="path-reward-dot">·</span>')}`;
  host.classList.remove('hidden');
}

/** Auth changed (sign-in / sign-out / delete): drop the cached streak so the
    next paint re-resolves it for the new identity. */
export function forgetPathStreak(): void {
  knownStreak = null;
  knownStats = null;
}
